import { useCallback, useMemo, useState } from 'react';
import { useOnboarding } from '@is24/tenant-network-fe/onboarding-widget';
import {
  IS24,
  ONBOARDING_QUERY_PARAM,
  ONBOARDING_QUERY_VALUE,
  resolveIS24BaseUrl,
  resolveListingState,
} from './mieterNetzwerk.utils';
import { useGetTenantNetworkListing, useGetTenantNetworkStatus } from '@/apiHooks/tenantNetwork.hook';
import { useFeature } from '@/apiHooks/featureSwitches.hook';
import { isFeatureFlagEnabled } from '@/utils/isFeatureFlagEnabled.util';
import { Feature } from '@/@types/featureSwitch';

export const useIsTenantNetworkEnabled = () => {
  const remote = useFeature(Feature.TENANT_NETWORK_WIDGET);
  const local = isFeatureFlagEnabled('TENANT_NETWORK_WIDGET');
  return remote || local;
};

export const useTenantNetworkController = (isVisible: boolean, onClose: () => void) => {
  const { initializing, tenantNetworkStatus } = useGetTenantNetworkStatus(isVisible);
  const propertyHubId = tenantNetworkStatus?.propertyHubId ?? null;

  const { tenantNetworkListing } = useGetTenantNetworkListing(
    propertyHubId,
    isVisible && tenantNetworkStatus !== undefined && !!propertyHubId,
  );

  const listingState = useMemo(
    () => resolveListingState(tenantNetworkStatus?.status, tenantNetworkListing?.status),
    [tenantNetworkStatus?.status, tenantNetworkListing?.status],
  );

  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  const handleOnboardingClose = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(ONBOARDING_QUERY_PARAM);
    window.history.replaceState({}, '', url);
    setIsOnboardingActive(false);
    onClose();
  }, [onClose]);

  const { open: openOnboarding } = useOnboarding({
    coreCssVersion: '8.4.0' as const,
    type: 'PROPERTY_HUB' as const,
    onClose: handleOnboardingClose,
    onComplete: handleOnboardingClose,
  });

  const handleOpenMeinImmobilien = useCallback(() => {
    const path = propertyHubId ? `${IS24.PATHS.meinImmobilien}/${propertyHubId}` : IS24.PATHS.meinImmobilienOverview;
    window.open(`${resolveIS24BaseUrl()}${path}`, '_blank', 'noopener,noreferrer');
  }, [propertyHubId]);

  const handleOpenHomepage = useCallback(() => {
    window.open(`${resolveIS24BaseUrl()}${IS24.PATHS.homepage}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleStartOnboarding = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set(ONBOARDING_QUERY_PARAM, ONBOARDING_QUERY_VALUE);
    window.history.replaceState({}, '', url);
    setIsOnboardingActive(true);
    openOnboarding();
  }, [openOnboarding]);

  return {
    initializing,
    listingState,
    isOnboardingActive,
    handleOpenMeinImmobilien,
    handleOpenHomepage,
    handleStartOnboarding,
  };
};
